import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { inject, observer } from 'mobx-react';
import '../../../assets/fonts/Montserrat/stylesheet.css';
import '../../../assets/fonts/Roboto/stylesheet.css';
import PropTypes from "prop-types";
import { brandContext } from "../../../store/brandContext";
import {
  BRANDS,
  htmlLangAttributes,
  defineCountryTextCode,
  defineIsDirRTL,
  defineIsRegFormNeeded,
  getLang,
} from "../../../common";
import { getCurrencyData } from "../../../api/api";
import { throwParamsToSite } from "../../../helpers";
import { translation } from "../../../translations/bonus30or35/translation";
import Header from "../../templateElements/header";
import Footer from "../../templateElements/footer";
import ZenbetBonus from "./-zenbetBonus";
import ZenitBonus from "./-zenitBonus";
import "./scss/style.scss";
import "./scss/_media.scss";


/**
 * Общий компонент для бонуса 30к и 35к
 *
 * @component
 * @param {Object} props
 * @param {Number} props.sumValue Сумма бонуса в валюте
 * @param {String} props.currency Валюта
 * @param {Object} props.promo Глобальный объект с информацией о промокоде
 * @param {String} lang Альфа-код языка
 * @param {Array} props.links Массив ссылок на соцсети
 * @param {Array} props.payArray Массив платежек
 *
 * @returns {function} Возвращает требуемый лендинг
 */
const Bonus30or35 = inject('appStore')(observer((props) => {
  const { appStore, promo, lang, partnerName, currency, sumValue, payArray, landingName } = props;
  const brandName = useContext(brandContext);
  const langAttr = htmlLangAttributes[lang];
  const [countryData, setCountryData] = useState(null);
  const [countryDataLangId, setCountryDataLangId] = useState(0);
  const [currencyData, setCurrencyData] = useState(null);
  const [isDirRTL, setIsDirRTL] = useState(false);
  const [isLangSpecialSiteTitle, setIsLangSpecialSiteTitle] = useState(false);
  const [siteTitle, setSiteTitle] = useState('');
  const [isRegFormNeeded, setIsRegFormNeeded] = useState(false);

  const arrLangSpecialSiteTitle = brandName === BRANDS.zenit ? ['kz', 'ru', 'ua'] : ['br', 'en'];

  const defineIsSpecialSiteTitle = () => {
    if (arrLangSpecialSiteTitle && arrLangSpecialSiteTitle.find(item => item === lang)) {
      setIsLangSpecialSiteTitle(true);
    }
  }

  const getText = textKey => `${getLang(translation, brandName, lang, textKey)}`

  const getTitle = () => {
    if (isLangSpecialSiteTitle) {
      if (promo.promo_code === 'bonus30k') {
        setSiteTitle(getText('siteTitle30'));
      }
      if (promo.promo_code === 'bonus35k' || promo.promo_code === 'tvshows' && lang === 'ru') {
        setSiteTitle(getText('siteTitle35'));
      }
    } else {
      setSiteTitle(getText('siteTitle'));
    }
  }

  useEffect(() => {
    getTitle();
  }, [isLangSpecialSiteTitle]);

  useEffect(() => {
    setIsDirRTL(defineIsDirRTL(lang));
    defineIsSpecialSiteTitle();
    setIsRegFormNeeded(defineIsRegFormNeeded(brandName, lang));
  }, []);

  useEffect(() => {
    setCountryData(appStore.getCountryDataByName(defineCountryTextCode(lang)));
  }, [appStore.CountryDataList]);

  useEffect(() => {
    if (countryData) {
      if (lang === 'en') {
        setCountryDataLangId(2);
      } else {
        setCountryDataLangId(countryData.lang_id)
      }
    }
  }, [countryData]);

  useEffect(() => {
    if (countryData && countryDataLangId) {
      throwParamsToSite(promo.promo_code, countryData.id, countryDataLangId);
      getCurrencyData(countryData.id)
        .then(res => setCurrencyData(res))
        .catch(err => console.log(`Ошибка при запросе данных по валюте: ${err}`));
    }
  }, [countryData, countryDataLangId]);


  return (
    <>
      <Helmet>
        <html lang={langAttr} dir={isDirRTL ? "rtl" : "ltr"} />
        <title>{siteTitle}</title>
      </Helmet>
      <div className='body-wrapper'>
        <Header
          hasRules
          promoId={promo[brandName]}
          langId={countryDataLangId}
          title={getLang(translation, brandName, lang, 'rulesBtn')}
          container
          positionLogo='left'
          lang={lang}
        />
        {brandName === BRANDS.zenbet
          ? <ZenbetBonus
            partnerName={partnerName}
            brandName={brandName}
            lang={lang}
            langId={countryDataLangId}
            currency={currency}
            sumValue={sumValue}
            promoObject={promo}
            isRegFormNeeded={isRegFormNeeded}
            countryData={countryData?.id}
            currencyData={currencyData?.id}
          />
          : <ZenitBonus
            brandName={brandName}
            lang={lang}
            langId={countryDataLangId}
            currency={currency}
            sumValue={sumValue}
            promoObject={promo}
            isRegFormNeeded={isRegFormNeeded}
            countryData={countryData?.id}
            currencyData={currencyData?.id}
            landingName={landingName}
          />
        }
        <Footer
          hasPayments
          hasRules={false}
          hasCopyright
          lang={lang}
          payArray={payArray}
          bkString={getLang(translation, brandName, lang, 'bkString')}
          rightString={getLang(translation, brandName, lang, 'rightString')}
          container
        />
      </div>
    </>
  );
}));

Bonus30or35.propTypes = {
  sumValue: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  promo: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
  links: PropTypes.array,
  payArray: PropTypes.array
}

export default Bonus30or35;
